import { hasConsent } from './cookieConsent'
import { ensureKlaviyoQueue } from '@utils/klaviyo'
import { getAnalyticsContext } from '@utils/analytics/context'

const KLAVIYO_COMPANY_ID = process.env.GATSBY_STORE === 'PH' ? 'R88Gus' : 'NbG4xX'

const loadedScripts = {
	analytics: false,
	markAnalytics: false,
	gtm: false,
	awin: false,
	klaviyo: false,
	tripleWhale: false,
	globalE: false,
	reamaze: false,
}

// Helper function to check if script element exists in DOM
const scriptExists = (id) => {
	if (typeof document === 'undefined') return false
	return document.getElementById(id) !== null
}

export const deferIdle = (callback) => {
	if (typeof window === 'undefined') return
	if ('requestIdleCallback' in window) {
		window.requestIdleCallback(callback, { timeout: 3000 })
	} else {
		setTimeout(callback, 2000)
	}
}

export const deferInteraction = (callback) => {
	if (typeof window === 'undefined') return

	const events = ['pointerdown', 'keydown', 'touchstart', 'scroll']
	let fired = false

	const run = () => {
		if (fired) return
		fired = true
		events.forEach((eventName) => window.removeEventListener(eventName, run))
		callback()
	}

	events.forEach((eventName) => window.addEventListener(eventName, run, { once: true, passive: true }))
}

const loadGoogleAnalytics = () => {
	if (loadedScripts.analytics || scriptExists('analytics-google')) {
		// If script exists in DOM but flag is false, update flag
		if (scriptExists('analytics-google')) {
			loadedScripts.analytics = true
		}
		return
	}

	// Load gtag.js
	const gtagScript = document.createElement('script')
	gtagScript.async = true
	gtagScript.src = process.env.GATSBY_STORE !== "PH" ? 'https://www.googletagmanager.com/gtag/js?id=G-LDLKZPZDJG' : 'https://www.googletagmanager.com/gtag/js?id=G-Z9RZHKY7T2'
	gtagScript.id = 'analytics-google'
	document.head.appendChild(gtagScript)

	// Initialize gtag
	window.dataLayer = window.dataLayer || []
	function gtag() {
		window.dataLayer.push(arguments)
	}
	window.gtag = gtag
	const analyticsContext = getAnalyticsContext()
	gtag('js', new Date())
	gtag('config',  process.env.GATSBY_STORE !== "PH" ? 'G-LDLKZPZDJG' : 'G-Z9RZHKY7T2', analyticsContext)
	gtag('set', 'user_properties', { site_language: analyticsContext.site_language })

	loadedScripts.analytics = true
}

const loadGoogleTagManager = () => {
	if (loadedScripts.gtm || scriptExists('googleTagManager-tracking')) {
		// If script exists in DOM but flag is false, update flag
		if (scriptExists('googleTagManager-tracking')) {
			loadedScripts.gtm = true
		}
		return
	}

	// Initialize dataLayer if not exists
	window.dataLayer = window.dataLayer || []
	window.dataLayer.push({
		'gtm.start': new Date().getTime(),
		event: 'gtm.js',
	})
	window.dataLayer.push({ ...getAnalyticsContext() })

	// Load GTM script
	const gtmScript = document.createElement('script')
	gtmScript.async = true
	gtmScript.src = process.env.GATSBY_STORE !== "PH" ? 'https://www.googletagmanager.com/gtm.js?id=GTM-P68SD3B' :'https://www.googletagmanager.com/gtm.js?id=GTM-PMXJBRWX'
	gtmScript.id = 'googleTagManager-tracking'

	const firstScript = document.getElementsByTagName('script')[0]
	if (firstScript && firstScript.parentNode) {
		firstScript.parentNode.insertBefore(gtmScript, firstScript)
	} else {
		document.head.appendChild(gtmScript)
	}

	loadedScripts.gtm = true
}

const removeGoogleAnalytics = () => {
	// Disable gtag by overriding it
	if (window.gtag) {
		window.gtag = function () {
			// No-op: do nothing
		}
	}

	// Clear dataLayer
	if (window.dataLayer) {
		window.dataLayer = []
	}

	// Remove script tags
	const gtagScript = document.getElementById('analytics-google')
	if (gtagScript) {
		gtagScript.remove()
		loadedScripts.analytics = false
	} else {
		// Script doesn't exist, ensure flag is false
		loadedScripts.analytics = false
	}
}

const removeGoogleTagManager = () => {
	// Clear dataLayer
	if (window.dataLayer) {
		window.dataLayer = []
	}

	// Remove GTM script
	const gtmScript = document.getElementById('googleTagManager-tracking')
	if (gtmScript) {
		gtmScript.remove()
		loadedScripts.gtm = false
	} else {
		// Script doesn't exist, ensure flag is false
		loadedScripts.gtm = false
	}

	// Remove GTM noscript iframe if exists
	const gtmNoscript = document.getElementById('gtm-noscript')
	if (gtmNoscript) {
		gtmNoscript.remove()
	}
}

const loadAwinScript = () => {
	if (loadedScripts.awin || scriptExists('awin-script')) {
		// If script exists in DOM but flag is false, update flag
		if (scriptExists('awin-script')) {
			loadedScripts.awin = true
		}
		return
	}

	const awinScript = document.createElement('script')
	awinScript.async = true
	awinScript.defer = true
	awinScript.type = 'text/javascript'
	awinScript.id = 'awin-script'
	awinScript.src = 'https://www.dwin1.com/122458.js'
	document.body.appendChild(awinScript)

	loadedScripts.awin = true
}

const loadKlaviyoScript = () => {
	if (loadedScripts.klaviyo || scriptExists('klaviyo-onsite')) {
		if (scriptExists('klaviyo-onsite')) {
			loadedScripts.klaviyo = true
		}
		return
	}

	ensureKlaviyoQueue()
	
	const klaviyoScript = document.createElement('script')
	klaviyoScript.async = true
	klaviyoScript.type = 'text/javascript'
	klaviyoScript.id = 'klaviyo-onsite'
	klaviyoScript.src = `https://static.klaviyo.com/onsite/js/${KLAVIYO_COMPANY_ID}/klaviyo.js`

	document.head.appendChild(klaviyoScript)
	loadedScripts.klaviyo = true
}

const loadTripleWhaleScript = () => {
	const scriptId = 'triple-whale-pixel'
	const preconnectApiId = 'triple-whale-preconnect-api'
	const preconnectConfId = 'triple-whale-preconnect-conf'

	if (loadedScripts.tripleWhale || scriptExists(scriptId)) {
		if (scriptExists(scriptId)) {
			loadedScripts.tripleWhale = true
		}
		return
	}

	if (!scriptExists(preconnectApiId)) {
		const preconnectApi = document.createElement('link')
		preconnectApi.id = preconnectApiId
		preconnectApi.setAttribute('rel', 'dns-prefetch')
		preconnectApi.href = 'https://api.config-security.com/'
		preconnectApi.crossOrigin = 'anonymous'
		document.head.appendChild(preconnectApi)
	}

	if (!scriptExists(preconnectConfId)) {
		const preconnectConf = document.createElement('link')
		preconnectConf.id = preconnectConfId
		preconnectConf.setAttribute('rel', 'dns-prefetch')
		preconnectConf.href = 'https://conf.config-security.com/'
		preconnectConf.crossOrigin = 'anonymous'
		document.head.appendChild(preconnectConf)
	}

	const tripleWhaleScript = document.createElement('script')
	tripleWhaleScript.id = scriptId
	tripleWhaleScript.innerHTML = `
		window.TriplePixelData={TripleName:"black-bough-swim.myshopify.com",ver:"2.17",plat:"SHOPIFY",isHeadless:true},function(W,H,A,L,E,_,B,N){function O(U,T,P,H,R){void 0===R&&(R=!1),H=new XMLHttpRequest,P?(H.open("POST",U,!0),H.setRequestHeader("Content-Type","text/plain")):H.open("GET",U,!0),H.send(JSON.stringify(P||{})),H.onreadystatechange=function(){4===H.readyState&&200===H.status?(R=H.responseText,U.includes("/first")?eval(R):P||(N[B]=R)):(299<H.status||H.status<200)&&T&&!R&&(R=!0,O(U,T-1,P))}}if(N=window,!N[H+"sn"]){N[H+"sn"]=1,L=function(){return Date.now().toString(36)+"_"+Math.random().toString(36)};try{A.setItem(H,1+(0|A.getItem(H)||0)),(E=JSON.parse(A.getItem(H+"U")||"[]")).push({u:location.href,r:document.referrer,t:Date.now(),id:L()}),A.setItem(H+"U",JSON.stringify(E))}catch(e){}var i,m,p;A.getItem('"!nC\`')||(_=A,A=N,A[H]||(E=A[H]=function(t,e,a){return void 0===a&&(a=[]),"State"==t?E.s:(W=L(),(E._q=E._q||[]).push([W,t,e].concat(a)),W)},E.s="Installed",E._q=[],E.ch=W,B="configSecurityConfModel",N[B]=1,O("https://conf.config-security.com/model",5),i=L(),m=A[atob("c2NyZWVu")],_.setItem("di_pmt_wt",i),p={id:i,action:"profile",avatar:_.getItem("auth-security_rand_salt_"),time:m[atob("d2lkdGg=")]+":"+m[atob("aGVpZ2h0")],host:A.TriplePixelData.TripleName,plat:A.TriplePixelData.plat,url:window.location.href.slice(0,500),ref:document.referrer,ver:A.TriplePixelData.ver},O("https://api.config-security.com/event",5,p),O("https://api.config-security.com/first?host=black-bough-swim.myshopify.com&plat=SHOPIFY",5)))}}("","TriplePixel",localStorage);
	`

	document.head.appendChild(tripleWhaleScript)
	loadedScripts.tripleWhale = true
}

const loadGlobalEScripts = () => {
	// Check if all Global-e scripts exist
	const allScriptsExist = scriptExists('ge-storefront-js') && 
	                       scriptExists('custom-script') && 
	                       scriptExists('ge-script-js')

	if (loadedScripts.globalE || allScriptsExist) {
		// If scripts exist in DOM but flag is false, update flag
		if (allScriptsExist) {
			loadedScripts.globalE = true
		}
		return
	}

	// Load Global-e scripts in order: storefront -> custom -> main
	// 1. Storefront script
	if (!scriptExists('ge-storefront-js')) {
		const geStorefrontScript = document.createElement('script')
		geStorefrontScript.async = true
		geStorefrontScript.id = 'ge-storefront-js'
		geStorefrontScript.setAttribute('data-mid', '8uoe')
		geStorefrontScript.setAttribute('data-env', 'prd')
		geStorefrontScript.src = 'https://web.global-e.com/Scripts/Merchants/globale.merchant.storefront.js'
		document.body.appendChild(geStorefrontScript)
	}

	// 2. Custom script (sets GLBE_PARAMS)
	if (!scriptExists('custom-script')) {
		const customScript = document.createElement('script')
		customScript.id = 'custom-script'
		customScript.innerHTML = `
			console.log("Native JS script loaded in Gatsby!");

			var storedCountry = localStorage.getItem("userCountry");
			var parsedCountry = storedCountry ? JSON.parse(storedCountry) : {};

			var country = parsedCountry.countryCode || "US";
			var currency = parsedCountry.currencyCode || "USD";

			var GLBE_PARAMS = {  
				countryCode: country,
				currencyCode: currency,
				isExternal: true
			};

			console.log("GLBE_PARAMS:", GLBE_PARAMS);
		`
		document.body.appendChild(customScript)
	}

	// 3. Main Global-e integration script
	if (!scriptExists('ge-script-js')) {
		const geMainScript = document.createElement('script')
		geMainScript.async = true
		geMainScript.defer = true
		geMainScript.id = 'ge-script-js'
		geMainScript.src = 'https://crossborder-integration.global-e.com/resources/js/app?shop=black-bough-swim.myshopify.com'
		document.body.appendChild(geMainScript)
	}

	loadedScripts.globalE = true
}

const removeAwinScript = () => {
	const awinScript = document.getElementById('awin-script')
	if (awinScript) {
		awinScript.remove()
		loadedScripts.awin = false
	} else {
		// Script doesn't exist, ensure flag is false
		loadedScripts.awin = false
	}
}

const removeKlaviyoScript = () => {
	const klaviyoScript = document.getElementById('klaviyo-onsite')
	if (klaviyoScript) {
		klaviyoScript.remove()
	}
	loadedScripts.klaviyo = false
}

const removeTripleWhaleScript = () => {
	const tripleWhaleScript = document.getElementById('triple-whale-pixel')
	if (tripleWhaleScript) {
		tripleWhaleScript.remove()
	}

	const preconnectApi = document.getElementById('triple-whale-preconnect-api')
	if (preconnectApi) {
		preconnectApi.remove()
	}

	const preconnectConf = document.getElementById('triple-whale-preconnect-conf')
	if (preconnectConf) {
		preconnectConf.remove()
	}

	delete window.TriplePixelsn
	delete window.configSecurityConfModel
	loadedScripts.tripleWhale = false
}

const loadReamazeScripts = () => {
	// Reamaze chat widget is desktop-only — skip loading on mobile viewports.
	if (typeof window !== 'undefined' && window.innerWidth < 768) return

	// Check if all Reamaze scripts exist
	const allScriptsExist = scriptExists('reamaze-loader') && 
	                       scriptExists('reamaze-config') && 
	                       scriptExists('reamaze-shoutbox')

	if (loadedScripts.reamaze || allScriptsExist) {
		// If scripts exist in DOM but flag is false, update flag
		if (allScriptsExist) {
			loadedScripts.reamaze = true
		}
		return
	}

	// Load Reamaze scripts in order: loader -> config -> shoutbox
	// 1. Main Reamaze loader script
	if (!scriptExists('reamaze-loader')) {
		const reamazeLoader = document.createElement('script')
		reamazeLoader.type = 'text/javascript'
		reamazeLoader.async = true
		reamazeLoader.id = 'reamaze-loader'
		reamazeLoader.src = 'https://cdn.reamaze.com/assets/reamaze.js'
		document.body.appendChild(reamazeLoader)
	}

	// 2. Reamaze configuration script
	if (!scriptExists('reamaze-config')) {
		const reamazeConfig = document.createElement('script')
		reamazeConfig.type = 'text/javascript'
		reamazeConfig.id = 'reamaze-config'
		reamazeConfig.innerHTML = `
			var _support = _support || { 'ui': {}, 'user': {} };
			_support['account'] = 'black-bough-swim';
			_support['contact_custom_fields'] = _support['contact_custom_fields'] || {};
			_support['contact_custom_fields']['rmz_form_id_2692'] = {};
		`
		document.body.appendChild(reamazeConfig)
	}

	// 3. Reamaze shoutbox configuration script
	if (!scriptExists('reamaze-shoutbox')) {
		const reamazeShoutbox = document.createElement('script')
		reamazeShoutbox.type = 'text/javascript'
		reamazeShoutbox.id = 'reamaze-shoutbox'
		reamazeShoutbox.innerHTML = `
			var _support = _support || { 'ui': {}, 'user': {} };
			_support['account'] = 'black-bough-swim';
			_support['ui']['contactMode'] = 'default';
			_support['ui']['enableKb'] = 'true';
			_support['ui']['styles'] = {
				widgetColor: 'rgb(0, 0, 0)',
			};
			_support['ui']['shoutboxFacesMode'] = "default";
			_support['ui']['shoutboxHeaderLogo'] = true;
			_support['ui']['widget'] = {
				icon: 'help',
				displayOn: 'all',
				label: false,
				position: {
				bottom: '100px',
				left: '15px'
				},
				size: 60,
				mobilePosition: {
				bottom: '100px',
				right: '22px'
				}
			};
			_support['apps'] = {
				faq: {"enabled":true},
				recentConversations: {},
				orders: {"enabled":true,"enable_notes":true}
			};
		`
		document.body.appendChild(reamazeShoutbox)
	}

	loadedScripts.reamaze = true
}


export const loadAnalyticsScripts = () => {
	if (typeof window === 'undefined') return

	if (!hasConsent('analytics')) {
		removeAnalyticsScripts()
		return
	}

	loadGoogleAnalytics()
	loadGoogleTagManager()
}


export const removeAnalyticsScripts = () => {
	if (typeof window === 'undefined') return

	removeGoogleAnalytics()
	removeGoogleTagManager()
}


export const loadMarketingScripts = () => {
	if (typeof window === 'undefined') return

	if (!hasConsent('marketing')) {
		removeMarketingScripts()
		return
	}

	loadAwinScript()
	loadKlaviyoScript()
	loadTripleWhaleScript()
}


export const removeMarketingScripts = () => {
	if (typeof window === 'undefined') return

	removeAwinScript()
	removeKlaviyoScript()
	removeTripleWhaleScript()
}


export const initializeScripts = () => {
	if (typeof window === 'undefined') return

	// Essential scripts (always load, no consent required)
	loadReamazeScripts()
    loadGlobalEScripts()

	// Analytics scripts (require analytics consent)
	if (hasConsent('analytics')) {
		loadAnalyticsScripts()
	} else {
		removeAnalyticsScripts()
	}

	// Marketing scripts (require marketing consent)
	if (hasConsent('marketing')) {
		loadMarketingScripts()
	} else {
		removeMarketingScripts()
	}
}
